// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withApollo } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
// import * as Sentry from '@sentry/browser'

import {
  Button,
  FileSelector,
  Modal,
  Heading,
  SvgIcon
} from '../../'

import { FILETYPE_EMPLOYEE_PAYSLIP } from '../../../constants'
import { decodeBase64 } from '../../../utils'


class EmployerPayslipFileUploader extends Component {
  static propTypes = {
    employerId: PropTypes.string,
    title: PropTypes.string,
    uploadButtonText: PropTypes.string,
    onCancel: PropTypes.func,
    onSucess: PropTypes.func,
    className: PropTypes.string
  }

  static defaultProps = {
    title: 'Choose files to upload:',
    className: ''
  }

  state = {
    numberOfFilesToUpload: 0,
    numberOfFilesUploaded: 0,
    unknownIDNumbers: [],
    invalidDates: [],
    invalidFileNameFormat: [],
    failedUploads: [],
    summaryCopiedToClipboard: false
  }

  getIdNumberFromFileName = (file) => (
    file.name.split('_')[0]  // expected file format is {id number}_{employee number}_{date}.pdf
  )

  getDateFromFileName = (file) => (
    file.name.split('_')[2].split('.')[0]  // expected file format is {id number}_{employee number}_{date}.pdf
  )

  uploadingComplete = () => (
    (this.state.numberOfFilesUploaded === this.state.numberOfFilesToUpload) ||
    (this.state.numberOfFilesUploaded + this.state.unknownIDNumbers.length + this.state.invalidFileNameFormat.length + this.state.failedUploads.length === this.state.numberOfFilesToUpload)
  )

  linkToIdentityAndUploadFile = (file) => {
    const idNumber = this.getIdNumberFromFileName(file)
    const date = this.getDateFromFileName(file)

    if (!moment(date).isValid()) {
      this.setState(prevState => ({ invalidDates: [ ...prevState.invalidDates, date ]}))
    }

    if (idNumber) {
      return this.getAssociatedIdentity(idNumber)
        .then(({ data }) => this.uploadFile(file, data.identities.edges[0].node.id))
        .catch(() => this.setState(prevState => ({ unknownIDNumbers: [ ...prevState.unknownIDNumbers, idNumber ] })))
    } else {
      this.setState(prevState => ({ invalidFileNameFormat: [ ...prevState.invalidFileNameFormat, file.name ] }))
      return Promise.resolve()
    }
  }

  getAssociatedIdentity = (idNumber) => (
    // query identities to find matching identity
    this.props.client.query({
      query: gql`
        query($idNumber: String!, $employerId: UUID!) {
          identities(client_Division_Employer_Id: $employerId, identityValue: $idNumber, first: 1) {
            edges {
              node {
                id
              }
            }
          }
        }
      `,
      variables: { idNumber, employerId: decodeBase64(this.props.employerId) }
    })
  )

  uploadFile = (file, identityId) => {
    let body = new FormData()
    body.append('filetype', FILETYPE_EMPLOYEE_PAYSLIP)  // temp hard-coded
    body.append('file', file)
    body.append('processed', 'yes')  // any string here will be interpreted by files/views.py as True

    const relationships = [
      ['employer', decodeBase64(this.props.employerId)],
      ['identity', decodeBase64(identityId)]
    ]
    body.append('relationships', JSON.stringify(relationships))

    return fetch(`${process.env.REACT_APP_API_ROOT}/v1/file/`, {
      method: 'post',
      headers: {
        'Authorization': 'Token ' + localStorage.getItem('token') || null
      },
      body: body
    })
    .then(response => {
      if (response.ok) {  // covers 200-range of http responses
        console.log('Upload successful.')
        this.setState(prevState => (
          {
            numberOfFilesUploaded: prevState.numberOfFilesUploaded + 1
          }
        ))
      } else {
        // Sentry.captureMessage(`Upload failed for ${identityId}.  HTTP: ${response.status} - ${response.statusText}`)
        this.setState(prevState => ({ failedUploads: [...prevState.failedUploads, file.name] }))
      }
    })
    .catch(error => {
      // Sentry.captureMessage(`Upload failed for ${identityId} - ${error}`)
      this.setState(prevState => ({ failedUploads: [...prevState.failedUploads, file.name] }))
    })
  }

  uploadFiles = async (filesObj) => {
    this.setState({ numberOfFilesToUpload: Object.keys(filesObj).length })

    for (const file of Object.values(filesObj)) {
      await this.linkToIdentityAndUploadFile(file)
    }
  }

  handleSelection = (e) => {
    let field = e.target.id
    this.setState({ [field]: e.target.value })
  }

  saveUploadSummaryToClipboard = () => {
    const el = document.createElement('textarea')
    el.value = (`
      Number of files uploaded vs number of files submitted for upload:\n\
      ${this.state.numberOfFilesUploaded} vs ${this.state.numberOfFilesToUpload}\n\
      \n\
      Unknown ID numbers:\n\
      ${this.state.unknownIDNumbers.length > 0 ? this.state.unknownIDNumbers.join(',') : 'None'}\n\
      \n\
      Invalid dates:\n\
      ${this.state.invalidDates.length > 0 ? this.state.invalidDates.join(',') : 'None'}\n\
      \n\
      Invalid file name format:\n\
      ${this.state.invalidFileNameFormat.length > 0 ? this.state.invalidFileNameFormat.join(',') : 'None'}\n\
      \n\
      Failed uploads (due to other reasons):\n\
      ${this.state.failedUploads.length > 0 ? this.state.failedUploads.join(',') : 'None'}\n\
    `).replace(/  +/g, '')  // dedent
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    this.setState({ summaryCopiedToClipboard: true })
  }

  render() {
    return (
      <div className={this.props.className}>
        {this.state.numberOfFilesToUpload > 0
          ? <Modal
              body={
                <Heading className='slds-text-align_center slds-m-top_medium'>
                  {this.uploadingComplete()
                    ? <div>
                        <SvgIcon
                          variant='utility'
                          symbol='success'
                          size='small'
                          colour='success'
                          className='slds-p-right_small'
                        />
                        Upload complete.
                        <div className='u-font-size-smaller slds-m-around_medium slds-p-top_small slds-p-left_large slds-text-align_left'>
                          {`•  ${this.state.numberOfFilesUploaded} files uploaded`}<br />
                          {`•  ${this.state.unknownIDNumbers.length} not linked to employee (ID number not found)`}<br />
                          {`•  ${this.state.invalidDates.length} invalid dates were found`}<br />
                          {`•  ${this.state.invalidFileNameFormat.length} files had an invalid file name format`}<br />
                          {`•  ${this.state.failedUploads.length} files failed to upload`}<br />
                          <div className='slds-m-top_large'>
                            {this.state.summaryCopiedToClipboard
                              ? <div className='slds-text-body_small'>
                                  <SvgIcon
                                    variant='utility'
                                    symbol='check'
                                    size='xx-small'
                                    colour='success'
                                    className='slds-p-right_x-small'
                                  />
                                  Copied detailed info
                                </div>
                              : <Button
                                  disabled={this.state.summaryCopiedToClipboard}
                                  onClick={this.saveUploadSummaryToClipboard}>
                                  Copy detailed info to clipboard
                                </Button>
                            }
                          </div>
                        </div>
                      </div>
                    : `Uploading file ${this.state.numberOfFilesUploaded + 1} of ${this.state.numberOfFilesToUpload}...`
                  }
                </Heading>
              }
              footer={
                <div className='slds-text-align_center'>
                  <Button
                    type='brand'
                    disabled={!this.uploadingComplete()}
                    onClick={() => (
                      this.setState(
                        {
                          numberOfFilesToUpload: 0,
                          numberOfFilesUploaded: 0,
                          unknownIDNumbers: [],
                          invalidDates: [],
                          invalidFileNameFormat: []
                        },
                        this.props.onSuccess
                      )
                    )}>
                    OK
                  </Button>
                </div>
              }
            />
          : <div className='slds-m-top_large'>
              <div className='u-font-size-large slds-p-bottom_medium'>
                {this.props.title}
              </div>
              <FileSelector
                dropzone={true}
                integrated={true}
                buttonLabel={this.props.uploadButtonText}
                cancelButton={true}
                accept='application/pdf, image/png'
                onCancel={this.props.onCancel}
                value={this.state.numberOfFilesToUpload}
                onClick={filesObj => this.uploadFiles(filesObj)}
              />
            </div>
        }
      </div>
    )
  }
}

export default withApollo(EmployerPayslipFileUploader)



// WEBPACK FOOTER //
// ./src/components/messageplus/employer-payslip-file-uploader/index.js